
import { kbArticles, kbCategories } from '../../../mocks/knowledge-base';

interface KBArticleDetailProps {
  articleId: string;
  onBack: () => void;
}

export default function KBArticleDetail({ articleId, onBack }: KBArticleDetailProps) {
  const article = kbArticles.find((a) => a.id === articleId);
  if (!article) return null;

  const categoryLabel = kbCategories.find((c) => c.id === article.category)?.label ?? article.category;

  const categoryColors: Record<string, string> = {
    specs: 'bg-primary/10 text-primary',
    setup: 'bg-green-100 text-green-700',
    troubleshooting: 'bg-orange-100 text-orange-700',
    'best-practices': 'bg-secondary/10 text-secondary',
  };
  const colorClass = categoryColors[article.category] ?? 'bg-gray-100 text-gray-600';

  return (
    <article className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Article Header */}
      <div className="p-8 border-b border-gray-100">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-body text-gray-400 hover:text-primary transition-colors mb-6 cursor-pointer whitespace-nowrap"
          aria-label="Back to article list"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
          </div>
          Back to Knowledge Base
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${colorClass}`}>
            <i className={`${article.icon} text-2xl`} aria-hidden="true"></i>
          </div>
          <div>
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${colorClass}`}>
              {categoryLabel}
            </span>
          </div>
          <span className="text-xs text-gray-400 font-body ml-auto whitespace-nowrap">
            <i className="ri-time-line mr-1" aria-hidden="true"></i>
            {article.readTime} read
          </span>
        </div>

        <h2 className="font-heading font-bold text-2xl md:text-3xl text-secondary mb-3 leading-tight">
          {article.title}
        </h2>
        <p className="font-body text-gray-500 text-base leading-relaxed">{article.summary}</p>

        <div className="flex flex-wrap gap-2 mt-4">
          {article.tags.map((tag) => (
            <span key={tag} className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-100">
              #{tag}
            </span>
          ))}
        </div>
      </div>

      {/* Article Body */}
      <div className="p-8 space-y-10">

        {/* SPECS */}
        {'sections' in article && article.sections.map((section, si) => (
          <div key={si}>
            <h3 className="font-heading font-bold text-lg text-secondary mb-4 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center text-primary">
                <i className="ri-list-check-2 text-base" aria-hidden="true"></i>
              </div>
              {section.heading}
            </h3>
            <div className="rounded-xl overflow-hidden border border-gray-100">
              {section.items.map((item, ii) => (
                <div
                  key={ii}
                  className={`flex items-center justify-between px-5 py-3.5 ${
                    ii % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                  }`}
                >
                  <span className="font-body text-sm text-gray-500 font-medium">{item.label}</span>
                  <span className="font-body text-sm text-secondary font-semibold text-right max-w-xs">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* SETUP STEPS */}
        {'steps' in article && article.steps.map((step) => (
          <div key={step.number} className="flex gap-5">
            <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-primary text-white font-heading font-bold text-sm">
              {step.number}
            </div>
            <div className="flex-1 pb-8 border-b border-gray-100 last:border-0 last:pb-0">
              <h3 className="font-heading font-bold text-base text-secondary mb-2">{step.title}</h3>
              <p className="font-body text-sm text-gray-600 leading-relaxed mb-3">{step.description}</p>
              <div className="flex items-start gap-2 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
                <div className="w-4 h-4 flex items-center justify-center text-primary flex-shrink-0 mt-0.5">
                  <i className="ri-lightbulb-line text-sm" aria-hidden="true"></i>
                </div>
                <p className="font-body text-xs text-primary leading-relaxed">{step.tip}</p>
              </div>
            </div>
          </div>
        ))}

        {/* TROUBLESHOOTING */}
        {'issues' in article && article.issues.map((issue, idx) => (
          <div key={idx} className="rounded-xl border border-gray-100 overflow-hidden">
            <div className="bg-orange-50 px-5 py-3 border-b border-orange-100 flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center text-orange-500">
                <i className="ri-error-warning-line text-sm" aria-hidden="true"></i>
              </div>
              <span className="font-heading font-semibold text-sm text-orange-700">{issue.problem}</span>
            </div>
            <div className="px-5 py-4 grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Likely Cause</p>
                <p className="font-body text-sm text-gray-600">{issue.cause}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Solution</p>
                <p className="font-body text-sm text-gray-700">{issue.solution}</p>
              </div>
            </div>
          </div>
        ))}

        {/* BEST PRACTICES */}
        {'practices' in article && article.practices.map((practice, pi) => (
          <div key={pi}>
            <h3 className="font-heading font-bold text-base text-secondary mb-3 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center text-primary">
                <i className="ri-checkbox-circle-line text-base" aria-hidden="true"></i>
              </div>
              {practice.title}
            </h3>
            <ul className="space-y-2">
              {practice.items.map((item, ii) => (
                <li key={ii} className="flex items-start gap-3 font-body text-sm text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center text-primary flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-sm" aria-hidden="true"></i>
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Footer CTA */}
      <div className="px-8 py-6 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="font-body text-sm text-gray-500">Was this article helpful? Contact our team for more support.</p>
        <a
          href="/contato"
          className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-full font-body font-semibold text-sm hover:bg-opacity-90 transition-all whitespace-nowrap cursor-pointer"
        >
          <i className="ri-customer-service-2-line" aria-hidden="true"></i>
          Talk to a Specialist
        </a>
      </div>
    </article>
  );
}
